import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  const handleNetworkError = useHandleNetworkError("Error creating patient");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async (patientData) => {
      const axiosInstance = createAxiosInstance();

      const address = patientData.address || {};

      let formattedDateOfDeath = null;
      if (patientData.dateOfDeath) {
        if (typeof patientData.dateOfDeath === "string") {
          if (patientData.dateOfDeath.match(/^\d{4}-\d{2}-\d{2}$/)) {
            formattedDateOfDeath = patientData.dateOfDeath;
          } else {
            const parsed = moment(patientData.dateOfDeath);
            if (parsed.isValid()) {
              formattedDateOfDeath = parsed.format("YYYY-MM-DD");
            }
          }
        } else if (Array.isArray(patientData.dateOfDeath)) {
          const [year, month, day] = patientData.dateOfDeath;
          formattedDateOfDeath = moment([year, month - 1, day]).format("YYYY-MM-DD");
        }
      }

      const tenantNumber =
        typeof tenantVal === "string" ? parseInt(tenantVal, 10) : tenantVal;

      const payload = {
        ...patientData,
        id: undefined,
        dateOfBirth: patientData.dateOfBirth
          ? moment(patientData.dateOfBirth).format("YYYY-MM-DD")
          : null,
        dateOfDeath: formattedDateOfDeath,
        address: {
          id: undefined,
          tenant: tenantNumber,
          addressLine1: address.addressLine1 || "",
          addressLine2: address.addressLine2 || "",
          addressLine3: address.addressLine3 || "",
          city: address.city || "",
          county: address.county || "",
          postCode: address.postCode || "",
          country: address.country || "",
        },
        addressId: undefined,
      };

      const response = await axiosInstance.post(`${tenantVal}/patient`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Patient duplicated successfully");
      queryClient.invalidateQueries();
    },
    onError: handleNetworkError,
  });
};
